import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { usePageTitle } from '../hooks/usePageTitle';
import { Link } from 'react-router-dom';
import { Search } from 'lucide-react';
import { Locale } from '@hebcal/core';
import { useShuls } from '../hooks/useShuls';
import { useZmanim } from '../hooks/useZmanim';
import { resolveAllSchedules } from '../utils/scheduleResolver';
import { detectDayType, getApplicableDayTypes } from '../utils/dayTypeDetector';
import { computeZmanimForDate } from '../utils/hebcalZmanim';
import { isSelichotSeason, isYomKippurChipWindow, isYizkorEligible } from '../utils/hebrewSeason';
import { planPrayer } from '../utils/minyanDay';
import { PRAYER_TYPES } from '../config/constants';
import { getTodayString } from '../utils/dateShortcuts';
import { addDaysToDate } from '../utils/israelTime';
import { DayType, PrayerType, ResolvedScheduleTime, ZmanimData } from '../types';

/**
 * Label for a lookahead (tomorrow) row: primary detected day type via dayTypes.*,
 * except when that type is yomTov — then prefer the specific chag name (e.g. Sukkot)
 * so a Shabbos+YomTov day is not misread as plain Shabbos.
 */
function getLookaheadDayLabel(
  zmanim: ZmanimData,
  dayType: DayType,
  t: (key: string) => string,
  lang: 'en' | 'he'
): string {
  if (dayType === 'yomTov' && zmanim.yomTovName) {
    const base = zmanim.yomTovName.replace(/\s+I{1,3}$/, '');
    const localized = Locale.gettext(base, lang);
    if (localized) return localized;
  }
  return t(`dayTypes.${dayType}`);
}

type SearchResult = ResolvedScheduleTime & {
  /** Shown when Friday/Erev YT lookahead merge is active. */
  dayLabel?: string;
};

/** Prayer chips for this page only — never Musaf; seasonal / Friday-only extras. */
function getFilterChips(
  dateStr: string,
  applicableDayTypes: DayType[],
  zmanim: ZmanimData | null
): PrayerType[] {
  return PRAYER_TYPES.filter((p) => {
    if (p === 'musaf') return false;
    if (p === 'selichot') return isSelichotSeason(dateStr);
    if (p === 'yizkor') return zmanim ? isYizkorEligible(zmanim) : false;
    if (p === 'kabalatShabbat') return applicableDayTypes.includes('erevShabbos');
    if (p === 'tefilahZakha' || p === 'neila') return isYomKippurChipWindow(dateStr);
    if (p === 'tekiot' || p === 'megillah') {
      return planPrayer(p, dateStr).levels.length > 0 || planPrayer(p, dateStr, 'start').levels.length > 0;
    }
    return true;
  });
}

export default function MinyanSearchPage() {
  const { t, i18n } = useTranslation();
  usePageTitle(t('seo.minyanSearch'), { description: t('seo.desc.minyanSearch') });
  const isHebrew = i18n.language === 'he';
  const { shuls, loading: shulsLoading } = useShuls();
  const { zmanim, loading: zmanimLoading } = useZmanim();
  const [query, setQuery] = useState('');
  const [prayerFilter, setPrayerFilter] = useState<PrayerType | 'all'>('all');

  const loading = shulsLoading || zmanimLoading;
  const today = getTodayString();

  const applicableDayTypes = useMemo(
    () => (zmanim ? getApplicableDayTypes(zmanim) : []),
    [zmanim]
  );

  const filterChips = useMemo(
    () => getFilterChips(today, applicableDayTypes, zmanim),
    [today, applicableDayTypes, zmanim]
  );

  const allTimes = useMemo(() => {
    if (!zmanim || shuls.length === 0) return [] as SearchResult[];

    const dayType = detectDayType(zmanim);
    const todayIsErevShabbos = applicableDayTypes.includes('erevShabbos');
    const todayIsErevYomTov = applicableDayTypes.includes('erevYomTov');
    // Unified lookahead: one merge when either (or both) eve types apply — never two merges.
    const mergeLookahead = todayIsErevShabbos || todayIsErevYomTov;

    const todayRows: SearchResult[] = resolveAllSchedules(
      shuls,
      today,
      dayType,
      zmanim
    ).map((row) =>
      mergeLookahead ? { ...row, dayLabel: t('minyanSearch.dayToday') } : row
    );

    if (!mergeLookahead) return todayRows;

    const tomorrow = addDaysToDate(today, 1);
    const tomorrowZmanim = computeZmanimForDate(tomorrow);
    const tomorrowDayType = detectDayType(tomorrowZmanim);
    const lang = isHebrew ? 'he' : 'en';
    const tomorrowLabel = getLookaheadDayLabel(tomorrowZmanim, tomorrowDayType, t, lang);

    const tomorrowRows: SearchResult[] = resolveAllSchedules(
      shuls,
      tomorrow,
      tomorrowDayType,
      tomorrowZmanim
    ).map((row) => ({ ...row, dayLabel: tomorrowLabel }));

    return [...todayRows, ...tomorrowRows];
  }, [shuls, zmanim, today, applicableDayTypes, t, isHebrew]);

  const activePrayerFilter: PrayerType | 'all' =
    prayerFilter !== 'all' && !filterChips.includes(prayerFilter) ? 'all' : prayerFilter;

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();

    return allTimes
      .filter((m) => {
        if (activePrayerFilter !== 'all' && m.prayerType !== activePrayerFilter) return false;
        if (!q) return true;
        const note = (m.note || '').toLowerCase();
        const noteHe = m.noteHe || '';
        return (
          m.shulNameEn.toLowerCase().includes(q) ||
          m.shulNameHe.includes(query.trim()) ||
          note.includes(q) ||
          noteHe.includes(query.trim())
        );
      })
      .sort((a, b) => a.displayTime.localeCompare(b.displayTime));
  }, [allTimes, query, activePrayerFilter]);

  return (
    <div className="max-w-5xl mx-auto px-4 py-6 space-y-4">
      <h1 className="text-lg font-semibold text-ink">{t('minyanSearch.heading')}</h1>

      <div className="relative">
        <Search className="absolute top-1/2 -translate-y-1/2 start-3 w-4 h-4 text-ash pointer-events-none" />
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t('minyanSearch.searchPlaceholder')}
          className="w-full ps-9 pe-3 py-2 rounded-[10px] border border-ink bg-paper text-ink placeholder:text-ash focus:outline-none focus:ring-2 focus:ring-ink/30 focus:border-ink text-sm"
        />
      </div>

      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={() => setPrayerFilter('all')}
          className={`rounded-[10px] text-xs px-3 py-1.5 font-medium transition-colors ${
            activePrayerFilter === 'all' ? 'bg-ink text-paper' : 'bg-fog text-ink'
          }`}
        >
          {t('minyanSearch.allPrayers')}
        </button>
        {filterChips.map((p) => (
          <button
            key={p}
            type="button"
            onClick={() => setPrayerFilter(p)}
            className={`rounded-[10px] text-xs px-3 py-1.5 font-medium transition-colors ${
              activePrayerFilter === p ? 'bg-ink text-paper' : 'bg-fog text-ink'
            }`}
          >
            {t(`prayers.${p}`)}
          </button>
        ))}
      </div>

      {loading && (
        <div className="space-y-2">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-16 rounded-[16px] bg-fog animate-pulse" />
          ))}
        </div>
      )}

      {!loading && filtered.length === 0 && (
        <p className="text-center text-sm text-ash py-8">{t('minyanSearch.noResults')}</p>
      )}

      {!loading && filtered.length > 0 && (
        <div className="space-y-2">
          {filtered.map((m) => {
            const note = isHebrew ? m.noteHe || m.note : m.note || m.noteHe;
            return (
              <Link
                key={`${m.shulId}-${m.scheduleId}-${m.prayerType}-${m.displayTime}-${m.dayLabel ?? ''}`}
                to={`/shul/${m.shulId}`}
                className="block bg-paper border border-ink/10 rounded-[16px] p-3 hover:opacity-80 transition-opacity"
              >
                <div className="flex items-baseline justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-ink">
                      {t(`prayers.${m.prayerType}`)}
                      {m.dayLabel ? (
                        <span className="ms-2 text-xs font-medium text-ash">{m.dayLabel}</span>
                      ) : null}
                    </p>
                    <p className="text-sm text-ash truncate">
                      {isHebrew ? m.shulNameHe : m.shulNameEn}
                      {note ? ` · ${note}` : ''}
                    </p>
                  </div>
                  <p className="text-lg font-bold tabular-nums text-ink shrink-0">{m.displayTime}</p>
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
